import { Link } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import SectionHeading from "@/components/base/SectionHeading";
import { trustFeatures } from "@/mocks/siteContent";

export default function WhyUsPage() {
  return (
    <SiteLayout>
      <Seo
        title="Why Coastal Bizz Connect"
        description="See why locals and businesses trust Coastal Bizz Connect — verified listings, truly hyperlocal search, direct contact with no middlemen and honest, moderated reviews across coastal Karnataka."
        keywords="why Coastal Bizz Connect, trusted business directory Karnataka, verified local businesses, hyperlocal directory Mangaluru Udupi Karwar"
        path="/why-us"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Why Coastal Bizz Connect",
          url: `${SITE_URL}/why-us`,
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        }}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-10 md:py-12">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="Breadcrumb">
            <Link to="/" className="cursor-pointer transition-colors hover:text-primary-700">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-foreground-700">Why Us</span>
          </nav>
          <h1 className="mt-3 font-heading text-2xl font-extrabold text-foreground-950 md:text-4xl">
            Why Coastal Bizz Connect
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            A directory that locals actually rely on — verified listings, honest reviews and direct
            contact without any middlemen.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-16 md:py-20">
        <SectionHeading
          eyebrow="Why Coastal Bizz Connect"
          title="Built for trust, made for the coast"
          description="A directory that locals actually rely on — verified listings, honest reviews and direct contact without any middlemen."
          align="center"
        />

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {trustFeatures.map((feature) => (
            <article
              key={feature.id}
              className="flex flex-col rounded-lg border border-background-200 bg-background-50 p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                <i className={`${feature.icon} text-xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                {feature.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                {feature.description}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-start gap-5 px-6 py-10 md:flex-row md:items-center md:justify-between md:py-12">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              Ready to be found on the coast?
            </h2>
            <p className="mt-1.5 max-w-xl text-sm text-foreground-600">
              Join thousands of local businesses already reaching customers through Coastal Bizz
              Connect.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/directory"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-sm font-semibold text-foreground-700 transition-colors duration-200 hover:bg-background-50"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-list-check text-base" />
              </span>
              Browse the directory
            </Link>
            <Link
              to="/list-your-business"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-add-line text-base" />
              </span>
              List your business
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}